import { useMemo } from "react";
import { ChatToolType } from "_graphql-types-frontend/graphql";
import { useOptionalCurrentUser } from "../../utils/hooks";

/** Display metadata for a tool surface shown in the chat tools menu. */
export type ToolDisplay = {
  tool: ChatToolType;
  name: string;
  description: string;
};

/**
 * The full catalog of tools the UI knows how to display. Source-of-truth
 * for tool names + descriptions in the chat tools menu. Tools are filtered
 * to the per-user available subset by `useAvailableToolsDisplay`.
 */
const TOOLS_CATALOG: ToolDisplay[] = [
  {
    tool: ChatToolType.DocumentsRagTool,
    name: "Document RAG",
    description: "Augments response with Document content",
  },
  {
    tool: ChatToolType.NotesRagTool,
    name: "Note RAG",
    description: "Augments response with Note content",
  },
  {
    tool: ChatToolType.CitationTool,
    name: "Citation Tool",
    description: "Augments response with citations",
  },
  {
    tool: ChatToolType.CurrentPageTool,
    name: "Current Page",
    description:
      "Gives context about the current page the user is viewing to sirius chat.",
  },
  {
    tool: ChatToolType.SiriusMcp,
    name: "Sirius MCP",
    description:
      "Exposes MCP (Model Context Protocol) tools for extended capabilities",
  },
  {
    tool: ChatToolType.CalculatorTool,
    name: "Calculator",
    description:
      "Evaluate mathematical expressions including arithmetic, functions, units, and percentages",
  },
  {
    tool: ChatToolType.ScratchpadTool,
    name: "Scratchpad",
    description:
      "Durable working memory: the assistant saves large intermediate results and reads them back in slices, keeping long analyses within the context window",
  },
  {
    tool: ChatToolType.CsvExportTool,
    name: "CSV Export",
    description:
      "Export conversation data, tool results, or other information to CSV format",
  },
  {
    tool: ChatToolType.UpdatePreferencesTool,
    name: "Memory",
    description:
      "Allows the AI to remember your preferences for future conversations",
  },
  {
    tool: ChatToolType.AlphaVantageMcp,
    name: "Alpha Vantage",
    description: "Stock quotes, forex exchange rates, and technical indicators",
  },
  {
    tool: ChatToolType.ChartTool,
    name: "Charts",
    description: "Renders interactive charts inline in the conversation",
  },
  {
    tool: ChatToolType.AtlasTool,
    name: "Atlas",
    description:
      "Solve problems dynamically — compute, create files, fetch live data, and transform information",
  },
  {
    tool: ChatToolType.SpawnSubAgentTool,
    name: "Sub-Agents",
    description:
      "Spawn focused sub-agents for parallel tasks with independent models and tools",
  },
  {
    tool: ChatToolType.StageMutationsTool,
    name: "Stage Data Updates",
    description:
      "Stage reviewable data changes for approval — update investments, add performance, AUM, documents",
  },
  {
    tool: ChatToolType.ModifyMutationPlanTool,
    name: "Edit Staged Data Updates",
    description:
      "Edit staged data updates before approval",
  },
  {
    tool: ChatToolType.FeedbackTool,
    name: "Report a Problem",
    description:
      "File product feedback about Sirius as a GitHub issue for the team to triage",
  },
];

/**
 * How the tools menu groups tools. Shared so every surface that offers a tool
 * selection groups and labels them the same way — the agent form used to show
 * one flat list, which read as a different product.
 */
export const TOOL_CATEGORIES: {
  key: string;
  title: string;
  tools: ChatToolType[];
}[] = [
  {
    key: "knowledge-rag",
    title: "Knowledge & RAG",
    tools: [
      ChatToolType.DocumentsRagTool,
      ChatToolType.NotesRagTool,
      ChatToolType.CitationTool,
    ],
  },
  {
    key: "context-platform",
    title: "Context & Platform",
    tools: [ChatToolType.CurrentPageTool, ChatToolType.SiriusMcp],
  },
  {
    key: "market-data",
    title: "Market Data",
    tools: [ChatToolType.AlphaVantageMcp],
  },
  {
    key: "mutations",
    title: "Agentic Data Creation and Updates (Beta)",
    tools: [
      ChatToolType.StageMutationsTool,
      ChatToolType.ModifyMutationPlanTool,
    ],
  },
  {
    key: "utilities",
    title: "Utilities",
    tools: [
      ChatToolType.CalculatorTool,
      ChatToolType.CsvExportTool,
      ChatToolType.UpdatePreferencesTool,
      ChatToolType.ChartTool,
      ChatToolType.AtlasTool,
      ChatToolType.SpawnSubAgentTool,
    ],
  },
  {
    key: "feedback",
    title: "Feedback",
    tools: [ChatToolType.FeedbackTool],
  },
];

/**
 * Tools that are always offered to every user. Most tools live here; gated
 * tools (e.g. AtlasTool only for subscription users) are appended below.
 */
const ALWAYS_AVAILABLE: ChatToolType[] = [
  ChatToolType.CurrentPageTool,
  ChatToolType.CalculatorTool,
  ChatToolType.ScratchpadTool,
  ChatToolType.CsvExportTool,
  ChatToolType.SiriusMcp,
  ChatToolType.AlphaVantageMcp,
  ChatToolType.UpdatePreferencesTool,
  ChatToolType.ChartTool,
  ChatToolType.DocumentsRagTool,
  ChatToolType.NotesRagTool,
  ChatToolType.CitationTool,
  ChatToolType.SpawnSubAgentTool,
  ChatToolType.StageMutationsTool,
  ChatToolType.ModifyMutationPlanTool,
];

/**
 * Returns the tool catalog filtered to what's actually available to the
 * current user — currently driven by their `isSubscriptionUser` flag for
 * AtlasTool gating. Memoized on the flag so a fresh array isn't returned
 * on every render.
 */
export function useAvailableToolsDisplay(): ToolDisplay[] {
  // useOptionalCurrentUser (not the throwing useCurrentUser): this runs at the
  // top of the chat render, and the body already null-guards currentUser — a
  // transient empty cache (e.g. during user-assumption) must not crash chat.
  const currentUser = useOptionalCurrentUser();
  return useMemo(() => {
    const available = [...ALWAYS_AVAILABLE];
    if (currentUser?.flags.isSubscriptionUser) {
      available.push(ChatToolType.AtlasTool);
      available.push(ChatToolType.FeedbackTool);
    }
    return TOOLS_CATALOG.filter(t => available.includes(t.tool));
  }, [currentUser?.flags.isSubscriptionUser]);
}

/** The display name of a chat tool, falling back to its enum name. */
export function toolLabel(tool: ChatToolType): string {
  return TOOLS_CATALOG.find(t => t.tool === tool)?.name ?? tool;
}

export type ToolGroup = { key: string; title: string; tools: ToolDisplay[] };

/**
 * The tool catalog an AGENT may be granted, grouped the same way the chat
 * tools menu groups it, minus anything a sub-agent cannot run.
 */
export function useAgentAssignableToolGroups(
  /**
   * From `chatAgentCapabilities`. Passed in rather than hardcoded: the server
   * enforces this list, and a client copy that goes stale means offering a
   * control that silently does nothing.
   */
  blockedTools: ChatToolType[]
): ToolGroup[] {
  const available = useAvailableToolsDisplay();
  const blockedKey = blockedTools.join(",");
  return useMemo(() => {
    const blocked = new Set(blockedTools);
    const byType = new Map(available.map(t => [t.tool, t]));
    return TOOL_CATEGORIES.map(c => ({
      key: c.key,
      title: c.title,
      tools: c.tools
        .filter(t => !blocked.has(t))
        .map(t => byType.get(t))
        .filter((t): t is ToolDisplay => !!t),
    })).filter(g => g.tools.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, blockedKey]);
}
